import CustomCard from "components/Atoms/CustomCard"
import NewTag from "components/Atoms/NewTag"
import StatusIcon from "components/Atoms/StatusIcon"
import StyledLink from "components/Atoms/StyledLink"
import Error from "components/Common/Error"
import ReactTableHeader from "components/Common/ReactTableHeader"
import DynamicSelector from "components/Molecules/DynamicSelector"
import TablePagination from "components/Molecules/TablePagination"
import NewResourceButton from "components/Organisms/AddResourceButton"
import MutationDialog from "components/Organisms/MutationDialog"
import OverFlowMenu from "components/Organisms/OverFlowMenu"
import SerialColumn from "components/Organisms/table/SerialColumn"
import { useVehicleMasterListQuery } from "generated/graphql"
import { ALL_DEPARTMENTS } from "graphql/selector.queries.gql"
import { DELETE_VEHICLE } from "graphql/vehicle/vehicle.mutations.gql"
import { VEHICLE_LIST } from "graphql/vehicle/vehicle.queries.gql"
import { useSearchFilter } from "hooks/searchFilterHooks"
import { pick } from "lodash-es"
import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import ReactTable from "react-table"
import AppToaster from "utils/AppToaster"
import { Cell } from "../../vehicle/table/AssignedTable"
import ShowAlerts from "./ShowAlerts"
import VehicleActions from "./VehicleActions"
const UserDisplay = ({ user, vehicle, linkTo, type }) => {
  return (
    <span>
      <StyledLink
        className="vt-highlight"
        to={{
          pathname: `${linkTo}/${user && user.id ? user.id : "new"}`,
        }}
        state={vehicle}
      >
        {user ? user.fullName : `+ Add ${type}`}
      </StyledLink>
      {user && user.number && <p className="mb-0">{user.number}</p>}
    </span>
  )
}

const SpecificationsDisplay = ({ vehicleType }) => {
  return (
    <span>
      <p className="mb-0">
        <span className="vt-highlight">{vehicleType.name}</span> (
        {vehicleType.category})
      </p>
      <p className="mb-0">
        Capacity -<span className="vt-highlight">{vehicleType.capacity}</span>{" "}
        <small className="vt-kg">{vehicleType.capacityUnit?.codename}</small>
      </p>
    </span>
  )
}
export default function Vehicles() {
  const navigate = useNavigate()

  const [vehicle, setVehicle] = useState<any>("")
  const [popupType, setPopup] = useState(null)
  const COLUMNS = [
    SerialColumn,
    {
      accessor: "actions",
      maxWidth: 50,

      Cell: ({ original }) => {
        return (
          <VehicleActions
            refetch={refetch}
            onDelete={vehicle => {
              setVehicle(vehicle)
              setPopup("delete")
            }}
            vehicle={original}
          />
        )
      },
    },
    {
      Header: "Status",
      accessor: "status",
      width: 75,
      show: true,
      Cell: ({ original }) => {
        return (
          <StatusIcon
            values={{
              AV: { intent: "success", tooltip: "Available", text: "AV" },
              NA: { intent: "warning", tooltip: "Not Avaiable", text: "NA" },
              BR: { intent: "danger", tooltip: "Broken", text: "BR" },
              RP: { intent: "warning", tooltip: "Broken", text: "RP" },
              OT: { intent: "none", tooltip: "On Trip", text: "OT" },
            }}
            condition={original.status}
          />
        )
      },
    },
    {
      Header: "Registration No",
      accessor: "registrationNumber",
      width: 175,
      show: true,
      required: true,
      Cell({ value, original }) {
        return (
          <>
            <StyledLink to={original.id}>{value}</StyledLink>
            <NewTag createdAt={original.createdAt} />
          </>
        )
      },
    },
    {
      Header: "Owner",
      accessor: "owner",
      width: 150,
      show: true,
      Cell: row => {
        const { value } = row
        return (
          <p className={`${value?.status === "NA" ? "text-red-600" : null}`}>
            <UserDisplay
              vehicle={pick(row.original, [
                "id",
                "registrationNumber",
                "vehicleType",
              ])}
              type="owner"
              user={value}
              linkTo={`/d/registration/owner`}
            />
          </p>
        )
      },
    },
    {
      Header: "Driver",
      accessor: "driver",
      width: 150,
      show: true,
      Cell: row => {
        const { value } = row
        return (
          <UserDisplay
            vehicle={pick(row.original, [
              "id",
              "registrationNumber",
              "vehicleType",
            ])}
            type="driver"
            user={value}
            linkTo={`/d/registration/driver`}
          />
        )
      },
    },
    {
      Header: "Specifications",
      accessor: "vehicleType",
      width: 250,
      show: true,
      Cell: ({ value }) => {
        return <SpecificationsDisplay vehicleType={value} />
      },
    },
    {
      Header: "Estd. Average",
      accessor: "estimatedAverage",
      width: 120,
      show: true,
      Cell: ({ value }) => {
        return (
          <span>
            <span className="vt-highlight">{value}</span> Km/l
          </span>
        )
      },
    },

    {
      accessor: "currentContract",
      Header: "Contract",
      width: 120,
      Cell({ value, original }) {
        return (
          <>
            <StyledLink
              className="vt-highlight"
              to={{
                pathname: `/d/registration/contract/${value?.id ?? "new"}`,
              }}
              state={{
                id: original?.id,
                registrationNumber: original?.registrationNumber,
                vehicleType: original?.vehicleType,
              }}
            >
              {value?.id ? (
                <Cell title={value?.contractType?.name ?? "NA"} />
              ) : (
                "+ Add Contract"
              )}
            </StyledLink>
          </>
        )
      },
    },
    {
      accessor: "currentContract.totalOutstanding",
      Header: "Total Outstanding",
      width: 120,
      Cell({ value }) {
        return `₹ ${value ?? 0}`
      },
    },
    {
      accessor: "alerts",
      Header: "Alerts",
      width: 120,
      Cell({ value }) {
        return <ShowAlerts alerts={value} />
      },
    },
    {
      Header: "Departments",
      accessor: "departments",
      show: true,
      Cell: ({ value }) => {
        return (
          <OverFlowMenu
            items={value}
            icon="briefcase"
            onClick={item => {
              navigate(`/d/registration/department/${item.id}`)
            }}
          />
        )
      },
    },
  ]

  function handleParamChange(path, value) {
    if (path === "page") {
      setFilter(prev => ({
        ...prev,
        page: value,
      }))
    } else {
      setFilter(prev => ({
        ...prev,
        [path]: value,
        departmentId: null,
        departmentName: null,
        page: 0,
      }))
    }
  }

  const [{ page, search, departmentId, departmentName }, setFilter] =
    useSearchFilter()

  const { data, loading, error, refetch } = useVehicleMasterListQuery({
    variables: {
      departmentId,
      query: search ?? "",
      first: 12,
      skip: page * 12,
    },
    fetchPolicy: "network-only",
  })

  if (error) return <Error message={error.message} />
  return (
    <CustomCard className="w-full p-0" style={{ borderRadius: 7 }}>
      <ReactTableHeader
        title="Vehicle"
        icon="drive-time"
        value={search}
        onQueryChange={v => handleParamChange("search", v)}
      >
        <DynamicSelector
          extraItems={[
            {
              id: "vt_all_departments",
              name: "--All Departments--",
            },
          ]}
          query={ALL_DEPARTMENTS}
          className="mb-0 mr-6"
          renderText={() => departmentName ?? "Select Department"}
          onChange={(_, item) => {
            if (item?.id === "vt_all_departments") {
              setFilter(prev => ({
                ...prev,
                departmentId: null,
                departmentName: null,
              }))
            } else {
              setFilter(prev => ({
                ...prev,
                departmentId: item?.id,
                departmentName: item?.name,
                page: 0,
              }))
            }
          }}
        />
        <TablePagination
          className="mr-6"
          page={page}
          onPageChange={v => handleParamChange("page", v)}
        />

        <NewResourceButton module="vehicle" />
      </ReactTableHeader>

      <ReactTable
        loading={loading}
        manual
        sortable={false}
        showPagination={false}
        pages={15}
        page={page}
        pageSize={12}
        className="mt-0 text-center vt-master-table vt-vehicle-table"
        resizable={true}
        data={data && data.vehicleList}
        columns={COLUMNS}
      />

      <MutationDialog
        light
        icon="drive-time"
        title="Archive Vehicle"
        isOpen={popupType === "delete"}
        mutation={DELETE_VEHICLE}
        onCompleted={({ delVehicle }) => {
          const v = delVehicle && delVehicle.vehicle
          AppToaster.show({
            message: `Status for Vehicle ${v.registrationNumber} changed successfully`,
            intent: "success",
            icon: "tick",
          })
          setPopup(false)
        }}
        update={(cache, { data }) => {
          const variables = {
            query: search,
            first: 12,
            skip: page * 12,
          }
          try {
            const { allVehicles } = cache.readQuery({
              query: VEHICLE_LIST,
              variables,
            })

            cache.writeQuery({
              query: VEHICLE_LIST,
              variables,
              data: {
                allVehicles: allVehicles.filter(
                  v => parseInt(v.id) !== parseInt(data.delVehicle.vehicle.id)
                ),
              },
            })
          } catch (err) {
            return null
          }
        }}
        onClose={() => setPopup(null)}
        variables={{
          id: vehicle?.id,
        }}
        text="Archive"
        intent="warning"
      >
        <p>Are you sure you want to archive the given vehicle ?</p>
      </MutationDialog>
    </CustomCard>
  )
}
